import { bbox } from "@turf/turf";
import { MultiPolygon, Polygon } from "geojson";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import Map, { Layer, MapRef, Source } from "react-map-gl/mapbox";

import { type UUID } from "../../../../apis/types/dataLayerTypes";
import {
	FIELD_BOUNDARY_CSS_VAR,
	FIELD_BOUNDARY_FALLBACK_COLOR,
	PLOT_BOUNDARY_CSS_VAR,
	PLOT_BOUNDARY_FALLBACK_COLOR,
	TRANSPARENT_COLOR,
	generateGreyscalePalette,
	makeFeatureFillLayerProps,
	makeFeatureOutlineLayerProps,
	resolveCssVarColor,
} from "../../../../utilities/graphics";

const MAP_FITBOUNDS_ANIMATION_MS = 500;
const MAP_FITBOUNDS_PX = 20;

export interface DataLayerMapProps {
	dataLayerNumZones: number;
	fieldIds: UUID[]; // array of fieldIds to act as `key` for fieldBoundaries and plotBoundaries map layers
	flightIds: UUID[]; // array of flightIds to act as (secondary) `key` for fieldBoundaries and plotBoundaries
	dataLayerFeatures: GeoJSON.FeatureCollection<Polygon | MultiPolygon>; // preview of data layer upload/ zonation
	fieldBoundaries: GeoJSON.Feature<Polygon>[]; // list of selected Feature map layers
	plotBoundaries: GeoJSON.FeatureCollection<Polygon>[]; // list of selected FeatureCollection map layers
}

/**
 * Mapbox component for vector data layers, overlay with field boundaries and plot boundaries.
 */
const DataLayerMap = ({
	dataLayerNumZones,
	fieldIds,
	flightIds,
	dataLayerFeatures,
	fieldBoundaries,
	plotBoundaries,
}: DataLayerMapProps) => {
	const mapRef = useRef<MapRef>(null);
	const fieldBoundaryColor = useMemo(
		() => resolveCssVarColor(FIELD_BOUNDARY_CSS_VAR, FIELD_BOUNDARY_FALLBACK_COLOR),
		[],
	);
	const plotBoundaryColor = useMemo(() => resolveCssVarColor(PLOT_BOUNDARY_CSS_VAR, PLOT_BOUNDARY_FALLBACK_COLOR), []);

	/*
	 * Callbacks
	 */

	// OnLoad handler for mapbox: Sync map bounds with AOI by creating a FeatureCollection from the
	// list of field boundaries.
	const fitMapBoundsToDataLayerFeatures = useCallback(
		(durationMs: number) => {
			if (mapRef?.current) {
				const [minLng, minLat, maxLng, maxLat] = bbox(dataLayerFeatures);
				mapRef.current.fitBounds(
					[
						[minLng, minLat],
						[maxLng, maxLat],
					],
					{
						padding: MAP_FITBOUNDS_PX,
						duration: durationMs,
						animate: durationMs > 0,
						linear: false, // aka fly-to
						essential: true, // ignore reduced-motion settings
					},
				);
			}
		},
		[dataLayerFeatures],
	);

	const fitMapBoundsToSelectedPlotBoundaries = useCallback(() => {
		if (mapRef?.current && plotBoundaries.length > 0) {
			const [minLng, minLat, maxLng, maxLat] = bbox({
				type: "FeatureCollection" as const,
				features: plotBoundaries.flatMap((fc) => fc.features),
			});
			mapRef.current.fitBounds(
				[
					[minLng, minLat],
					[maxLng, maxLat],
				],
				{
					padding: MAP_FITBOUNDS_PX,
					duration: MAP_FITBOUNDS_ANIMATION_MS,
					animate: true,
					linear: false, // aka fly-to
					essential: true, // ignore reduced-motion settings
				},
			);
		}
	}, [plotBoundaries]);

	/*
	 * Effects
	 */

	// Re-fit to AOI when dataLayerFeatures changes
	useEffect(() => {
		fitMapBoundsToDataLayerFeatures(MAP_FITBOUNDS_ANIMATION_MS);
	}, [dataLayerFeatures, fitMapBoundsToDataLayerFeatures]);

	// Re-fit to AOI when selected field + show plots selection changes. By default none are shown.
	useEffect(() => {
		if (plotBoundaries.length > 0) {
			fitMapBoundsToSelectedPlotBoundaries();
		} else {
			fitMapBoundsToDataLayerFeatures(0);
		}
	}, [plotBoundaries, fitMapBoundsToSelectedPlotBoundaries, fitMapBoundsToDataLayerFeatures]);

	/*
	 * Memoization/derived values
	 */

	// Greyscale color map keyed by aerialfarmsStd id. Unassigned (id 0) → transparent.
	const targetAttrColors = useMemo(() => {
		const palette = generateGreyscalePalette(dataLayerNumZones);
		const result: Record<number, string> = { 0: TRANSPARENT_COLOR };
		for (let id = 1; id <= dataLayerNumZones; id++) {
			result[id] = palette[id - 1];
		}
		return result;
	}, [dataLayerNumZones]);

	const dataLayerMapStyle = makeFeatureFillLayerProps("data-layer", [
		"match",
		["get", "aerialfarmsStd"],
		..._.flatMap(Object.entries(targetAttrColors), ([id, color]) => [Number(id), color]),
		TRANSPARENT_COLOR,
	]);

	const dataLayerOutlineStyle = makeFeatureOutlineLayerProps("data-layer-outline", fieldBoundaryColor);

	const fieldBoundaryMapStyle = makeFeatureOutlineLayerProps(undefined, fieldBoundaryColor);

	const plotBoundariesMapStyle = makeFeatureOutlineLayerProps(undefined, plotBoundaryColor);

	const uniqueFieldFlightIds = _.zip(fieldIds, flightIds).map(([fieldId, flightId]) => `${fieldId}--${flightId}`);

	/*
	 * Rendering
	 */
	return (
		<Map
			ref={mapRef}
			onLoad={() => fitMapBoundsToDataLayerFeatures(0)}
			projection="mercator"
			mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
			mapboxAccessToken={process.env.MAPBOX_ACCESS_TOKEN}
		>
			<Source type="geojson" data={dataLayerFeatures}>
				<Layer {...dataLayerMapStyle} />
				<Layer {...dataLayerOutlineStyle} />
			</Source>
			{fieldBoundaries.map((feature, i) => {
				const uniqueId = uniqueFieldFlightIds[i];
				return (
					<Source type="geojson" data={feature} key={`field-boundary-for-flight-${uniqueId}`}>
						<Layer id={`field-boundary-${uniqueId}`} {...fieldBoundaryMapStyle} />
					</Source>
				);
			})}
			{plotBoundaries.map((featureCollection, i) => {
				const uniqueId = uniqueFieldFlightIds[i];
				return (
					<Source type="geojson" data={featureCollection} key={`plot-boundaries-for-flight-${uniqueId}`}>
						<Layer id={`plot-boundaries-${uniqueId}`} {...plotBoundariesMapStyle} />
					</Source>
				);
			})}
		</Map>
	);
};

export default DataLayerMap;
